import "aframe";
import "aframe-extras";
import "./globals.css";
import "./i18n";

import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookiesProvider } from "react-cookie";
import { createRoot } from "react-dom/client";

import { Scroll } from "./components/scroll";
import { Provider } from "./store";

// SHARED
const Header = React.lazy(() => import("./pages/_shared/header"));
const Footer = React.lazy(() => import("./pages/_shared/footer"));

const SessionLayout = React.lazy(() => import("./pages/layout"));
const HomePage = React.lazy(() => import("./pages/home"));
const ExpertLayout = React.lazy(() => import("./pages/_shared/expert/layout"));
const SimulationPage = React.lazy(() => import("./pages/simulation/page"));
const MaintainPage = React.lazy(() => import("./pages/maintain/page"));
const NotFound = React.lazy(() => import("./pages/not-found"));
const DocsPage = React.lazy(() => import("./pages/docs/page"));

// AUTH
const LoginPage = React.lazy(() => import("./pages/auth/page"));
const MFAPage = React.lazy(() => import("./pages/auth/mfa/page"));
const TermsPage = React.lazy(() => import("./pages/auth/mfa/terms/page"));
const SetupPage = React.lazy(() => import("./pages/auth/setup/page"));
const SetupCompPage = React.lazy(
  () => import("./pages/auth/setup/complete/page")
);
const ForgetPage = React.lazy(() => import("./pages/auth/forget/page"));
const ForgetCompPage = React.lazy(
  () => import("./pages/auth/forget/complete/page")
);

// ACCOUNT
const SetupAccountPage = React.lazy(
  () => import("./pages/account/setup/legacy")
);
const SetupAccountCompPage = React.lazy(
  () => import("./pages/account/setup/complete/page")
);
const SetupAccountWaitingPage = React.lazy(
  () => import("./pages/account/setup/waiting/page")
);
const AccountLayout = React.lazy(() => import("./pages/account/layout"));
const ProfilePage = React.lazy(() => import("./pages/account/profile/page"));
const MemberPage = React.lazy(() => import("./pages/account/member/page"));
const PlanLayout = React.lazy(() => import("./pages/account/plan/layout"));
const PlanPage = React.lazy(() => import("./pages/account/plan/(plan)/page"));
const PricePage = React.lazy(() => import("./pages/account/plan/price/page"));
const BillPage = React.lazy(() => import("./pages/account/plan/(plan)/legacy"));
const SupportPage = React.lazy(() => import("./pages/account/support/page"));
const InvoicePage = React.lazy(() => import("./pages/account/invoice/page"));

// DASHBOARD
const DashboardPage = React.lazy(() => import("./pages/dashboard/page"));

// GRAPH
const GraphPage = React.lazy(() => import("./pages/graph/page"));
const CausalGraph = React.lazy(() => import("./pages/graph/legacy/page"));

// RCA
const RcaPage = React.lazy(() => import("./pages/rca/page"));

// AUGMENTATION
const AugmentationPage = React.lazy(() => import("./pages/augmentation/page"));

// CAUSAL AI
const CausalAiPage = React.lazy(() => import("./pages/causal-ai/page"));

// DATA MANAGER
const DataManagerLayout = React.lazy(
  () => import("./pages/data-manager/layout")
);
const IdCodePage = React.lazy(
  () => import("./pages/data-manager/(idcode)/page")
);
const NodeLayout = React.lazy(() => import("./pages/data-manager/node/layout"));
const NodePage = React.lazy(
  () => import("./pages/data-manager/node/(node)/page")
);
const DiscretizePage = React.lazy(
  () => import("./pages/data-manager/node/discretize/page")
);
const DiscretizeView = React.lazy(
  () => import("./pages/data-manager/node/discretize/legacy")
);
const PriorLayout = React.lazy(
  () => import("./pages/data-manager/prior/layout")
);
const PriorPage = React.lazy(
  () => import("./pages/data-manager/prior/(prior)/page")
);
const PriorAssignPage = React.lazy(
  () => import("./pages/data-manager/prior/assign/page")
);
const PriorExtractPage = React.lazy(
  () => import("./pages/data-manager/prior/extract/page")
);
const PriorConsentPage = React.lazy(
  () => import("./pages/data-manager/prior/extract/consent/page")
);
const PriorSettingPage = React.lazy(
  () => import("./pages/data-manager/prior/setting/page")
);
const GroupPage = React.lazy(() => import("./pages/data-manager/group/page"));
const AttributeSettingsPage = React.lazy(
  () => import("./pages/data-manager/attribute/page")
);
const TimeGrainSettingsPage = React.lazy(
  () => import("./pages/data-manager/timegrain/page")
);

// ASSISTANT
const AssistantLayout = React.lazy(
  () => import("./pages/assistant/layout")
);
const AssistantPage = React.lazy(
  () => import("./pages/assistant/page")
);

const App: React.FC = () => {
  return (
    <Scroll>
      <Header />
      <main>
        <React.Suspense fallback={<div style={{ height: "100vh" }} />}>
          <Routes>
            <Route path="/:lang?">
              <Route index element={<NotFound />} />

              {/* AUTH */}
              <Route path="login" element={<LoginPage />} />
              <Route path="login/mfa" element={<MFAPage />} />
              <Route path="login/mfa/terms" element={<TermsPage />} />
              <Route path="setup" element={<SetupPage />} />
              <Route path="setup/complete" element={<SetupCompPage />} />
              <Route path="forget" element={<ForgetPage />} />
              <Route path="forget/complete" element={<ForgetCompPage />} />

              <Route element={<SessionLayout />}>
                {/* ACCOUNT */}
                <Route path="account/setup" element={<SetupAccountPage />} />
                <Route
                  path="account/setup/complete"
                  element={<SetupAccountCompPage />}
                />
                <Route
                  path="account/setup/waiting"
                  element={<SetupAccountWaitingPage />}
                />
                <Route path="account" element={<AccountLayout />}>
                  <Route index element={<ProfilePage />} />
                  <Route path="member" element={<MemberPage />} />
                  <Route path="bill" element={<BillPage />} />
                  <Route path="plan" element={<PlanLayout />}>
                    <Route index element={<PlanPage />} />
                    <Route path="price" element={<PricePage />} />
                  </Route>
                  <Route path="invoice" element={<InvoicePage />} />
                  <Route path="support" element={<SupportPage />} />
                </Route>

                {/* HOME (ログイン直後の振り分け) */}
                <Route path="home" element={<HomePage />} />

                {/* DASHBOARD */}
                <Route path="dashboard" element={<DashboardPage />} />

                <Route element={<ExpertLayout />}>
                  {/* GRAPH */}
                  <Route path="graph" element={<CausalGraph />} />
                  <Route path="v2/graph" element={<GraphPage />} />

                  {/* RCA */}
                  <Route path="rca" element={<RcaPage />} />

                  {/* AUGMENTATION */}
                  <Route path="augmentation" element={<AugmentationPage />} />

                  {/* CATE */}
                  <Route path="cate" element={<CausalAiPage />} />

                  {/* SIMULATION */}
                  <Route path="simulation" element={<SimulationPage />} />

                  {/* DATA MANAGER */}
                  <Route path="data-manager" element={<DataManagerLayout />}>
                    <Route index element={<IdCodePage />} />
                    <Route path="node" element={<NodeLayout />}>
                      <Route index element={<NodePage />} />
                      <Route path="discretize" element={<DiscretizeView />} />
                      <Route path="v2/discretize" element={<DiscretizePage />} />
                    </Route>
                    <Route path="prior" element={<PriorLayout />}>
                      <Route index element={<PriorPage />} />
                      <Route path="assign" element={<PriorAssignPage />} />
                      <Route path="extract">
                        <Route index element={<PriorExtractPage />} />
                        <Route path="consent" element={<PriorConsentPage />} />
                      </Route>
                      <Route path="setting" element={<PriorSettingPage />} />
                    </Route>
                    <Route path="group" element={<GroupPage />} />
                    <Route path="attribute" element={<AttributeSettingsPage />} />
                    <Route path="timegrain" element={<TimeGrainSettingsPage />} />
                  </Route>
                </Route>

                {/* ASSISTANT */}
                <Route path="assistant" element={<AssistantLayout />}>
                  <Route index element={<AssistantPage />} />
                </Route>

                {/* OTHERS */}
                <Route path="docs" element={<DocsPage />} />
              </Route>

              {/* FALLBACK */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </React.Suspense>
      </main>
      <Footer />
    </Scroll>
  );
};

const rootElement =
  document.getElementById("root") || document.createElement("div");
const root = createRoot(rootElement);

const isMaintain = false;

root.render(
  <React.Fragment>
    {isMaintain ? (
      <MaintainPage />
    ) : (
      <CookiesProvider>
        <BrowserRouter>
          <Provider>
            <App />
          </Provider>
        </BrowserRouter>
      </CookiesProvider>
    )}
  </React.Fragment>
);
